// src/components/QueuedInvoicesModal.jsx
import React, { useEffect, useState } from "react";
import { getAllQueueItems, dequeueItem } from "../services/offlineQueue";

export default function QueuedInvoicesModal({ open, onClose, onLoad }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const arr = await getAllQueueItems();
      // normalize: stored value may have been saved under { id, ...payload }
      const normalized = (arr || []).map((entry) => {
        // if offlineQueue returned { id: key, ...value } keep as-is
        // otherwise if it's raw payload, synthesize id
        return {
          id: entry.id || entry.key || `q_${Date.now()}_${Math.random().toString(36).slice(2,7)}`,
          // entry might include top-level fields or be nested; keep original payload as 'payload' for safety
          ...entry,
        };
      });
      setItems(normalized);
    } catch (err) {
      console.warn("Failed to load queued items", err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) refresh();
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-12 px-4">
      <div className="bg-white w-full max-w-4xl rounded shadow-lg overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center border-b px-4 py-3">
          <div className="font-semibold text-lg">Queued Invoices</div>
          <div className="flex items-center gap-3">
            <div className="text-xs text-gray-500">{loading ? "Loading…" : `${items.length} queued`}</div>
            <button className="text-sm text-red-600" onClick={onClose}>✖ Close</button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-auto max-h-[60vh]">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 sticky top-0">
              <tr>
                <th className="p-2 text-left">Client Token</th>
                <th className="p-2 text-left">Party</th>
                <th className="p-2 text-left">Date</th>
                <th className="p-2 text-right">Grand Total</th>
                <th className="p-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-6 text-center text-gray-500">No queued invoices 🎉</td>
                </tr>
              ) : (
                items.map((it) => {
                  // queued entry shape may vary; try to read common fields with fallbacks
                  const payload = it.payload || it.value || it; // some implementations store payload under 'value'/'payload'
                  const clientToken = payload?.clientToken || payload?.clienttoken || payload?.id || it.id || "-";
                  const party = payload?.selectedParty?.partyName || payload?.partyName || payload?.party || "-";
                  const dateRaw = payload?.invoiceDateStr || payload?.invoiceDate || payload?.date || null;
                  const dateStr = dateRaw ? (new Date(dateRaw).toLocaleDateString()) : "-";
                  const total = payload?.grandTotal || payload?.grandTotalRounded || payload?.totals?.net || 0;

                  return (
                    <tr key={it.id || clientToken} className="border-b hover:bg-gray-50">
                      <td className="p-2 max-w-xs truncate">{clientToken}</td>
                      <td className="p-2 max-w-xs truncate">{party}</td>
                      <td className="p-2">{dateStr}</td>
                      <td className="p-2 text-right">₹ {Number(total || 0).toFixed(2)}</td>
                      <td className="p-2 text-center space-x-2">
                        {/* Load into BillingScreen for edit */}
                        <button
                          className="text-xs px-2 py-1 border rounded bg-white hover:bg-gray-50"
                          disabled={busyId === it.id}
                          onClick={() => {
                            try {
                              // call parent callback with full payload (so it can load into form)
                              if (onLoad) onLoad(payload, it.id);
                              onClose?.();
                            } catch (err) {
                              console.error("onLoad failed", err);
                              alert("Failed to load queued invoice into editor (see console).");
                            }
                          }}
                        >
                          Load
                        </button>

                        {/* Delete from queue */}
                        <button
                          className="text-xs px-2 py-1 border rounded text-red-600 bg-white hover:bg-red-50"
                          disabled={busyId === it.id}
                          onClick={async () => {
                            if (!window.confirm("Remove this queued invoice permanently?")) return;
                            try {
                              setBusyId(it.id);
                              await dequeueItem(it.id);
                              await refresh();
                            } catch (err) {
                              console.error("Failed to delete queued item", err);
                              alert("Delete failed — check console.");
                            } finally {
                              setBusyId(null);
                            }
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="border-t px-4 py-3 flex items-center justify-between">
          <div className="text-xs text-gray-500">Tip: Load to edit, then Save to retry sync.</div>
          <div className="flex items-center gap-2">
            <button
              onClick={refresh}
              className="text-xs px-3 py-1 border rounded bg-gray-100"
            >
              Refresh
            </button>
            <button
              onClick={onClose}
              className="text-xs px-3 py-1 border rounded bg-blue-600 text-white"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
